import { utils } from '../utils.js';
import { myToolbar } from '../tools/toolbar.js';
import { beads } from '../beads.js';
import { settings } from '../settings.js';
import { menu } from '../menu.js';
import { browserFields, beadCloud } from '../core/azure.js';
import { getThumbnail } from '../procedures/thumbnail.js';
import { lang } from '../lang.js';
import { compress } from './compress.js';
import { load } from './load.js';
import { project } from './project.js';
import { libraries } from '../libraries.js';
import { patterns } from '../patterns/patterns.js';

export const save = {};

const getPaletteArr = () => b3.store.state.swatches.map(element => ({
    color: element.fillColor,
    finishName: element.finishName,
    inner: element.inner,
    description: element.description,
    secondColor: element.secondColor,
    letterCode: element.letterCode,
    id: element.id
}));

const getCameraSettings = () => ({
    scroll: [game.cameras.main.scrollX, game.cameras.main.scrollY],
    zoom: game.cameras.main.zoom,
    angle: settings.pattern.rotateCamera ?
        game.cameras.main.rotation :
        game.cameras.main.rotation + (Math.PI / 2)
});

const getBeadString = () => compress(beads.arr.filter(b => b.active));

const getBeadCount = () => beads.arr.filter(b => b.active).length;

const getSaveObject = ({
    dateCreated=utils.YYYYMMDD()
} = {}) => ({
    title: settings.title,
    lowerTitle: settings.title.toLowerCase(),
    description: settings.description,
    instructions: settings.instructions,
    credits: settings.credits,
    tags: settings.tags,
    beadCount: getBeadCount(),
    dateCreated,
    dateModified: utils.YYYYMMDD(),
    settings: {
        rows: settings.rows,
        columns: settings.columns,
        ignoreLastRow: settings.ignoreLastRow || false,
        beadShape: settings.library.shape,
        beadDisplayHeight: settings.beadDisplayHeight,
        diameter: settings.diameter,
        width: settings.width,
        patternName: settings.pattern.name,
        offset: beads.rowOffset.join(''),
        libraryIndex: settings.library.index,
    },
    beadString: getBeadString(),
    swatch: getPaletteArr(),
    camera: getCameraSettings(),
    thumbnail: getThumbnail(),
    userId: currentUserId,
    version: 3
});

save.newProject = async config => {
    if (!loggedIn) return;

    save.startAutoSave();

    if (config.loading) return;
    const recordObj = getSaveObject();
    const { newID } = await beadCloud({
        fn: 'saveToCloud',
        params: {recordObj, container: 'projects'}
    });
    if (!newID) {
        console.warn('Could not save project');
        return;
    }
    settings.currentProjectId = newID;
    recordObj.id = newID;
    b3.store.mySet('openFile', recordObj);
    project.clearCache();
};

const updateCloud = ({id, recordObj}) => {
    const response = beadCloud({
        fn: 'updateItemInCloud',
        params: {id, recordObj, container: 'projects'}
    });
    const warning = typeof response.error === 'string';
    const msg = warning ?
        lang.get('error saving project', 'Error saving project') + ': ' + response.error:
        lang.get('project saved to cloud', 'Project saved to cloud');
    menu.showInfoBox({msg, warning});
};

save.currentlyOpenFile = ({reload=false, forceSave=false} = {}) => {

    const id = settings.currentProjectId;

    if (loggedIn) {
        const cloudTool = b3.store.state.tools.find(element => element.name === 'Cloud');
        if (cloudTool.disabled && !reload && !forceSave) {
            return;
        };
        myToolbar.disableTool('Cloud');
        window.onbeforeunload = null; // no longer warn user about unsaved work
    }

    // create object
    const openFile = b3.store.state.openFile;
    const recordObj = getSaveObject({dateCreated: openFile.dateCreated});
    recordObj.id = id;

    if (loggedIn) {
        // update in cloud
        updateCloud({id, recordObj});

        // update browser cache
        project.updateResultCache({id, recordObj});
    }


    if (!reload) return;

    load.project({
        fileObject: recordObj,
        keepImage: true
    });
};

save.getFileArr = async files => {
    const result = [];

    for (const file of files) {
        let myFile = await beadCloud({
            fn: 'getItemFromId',
            params: {
                id: file.id,
                container: 'projects'
            }
        });
        if (myFile?.error) {
            console.warn('error loading file');
        } else if (myFile) {
            result.push(myFile);
        };
    }

    return result;
};

save.downloadMultiple = async files => {

    const zip = new JSZip();

    const fileArr = await save.getFileArr(files);

    fileArr.forEach(file => {
        zip.file(file.title + '.bead', JSON.stringify(file));
    });

    zip.generateAsync({type:'blob'})
        .then(blob => saveAs(blob, 'archive.zip'))
        .catch(err => console.error(err));
};

save.downloadById = async id => {
    const myFile = await beadCloud({
        fn: 'getItemFromId',
        params: {
            id,
            container: 'projects'
        }
    });
    if (!myFile) return;
    if (myFile.error) {
        console.warn('error downloading file');
    }

    save.download(JSON.stringify(myFile), myFile.lowerTitle + '.bead');
};

save.download = (saveObject, title='myproject') => {

    const blob = new Blob([saveObject], {type: 'text/plain;charset=utf-8'});
    const myDownload = document.getElementById('my-download');
    myDownload.onclick = function (event) {
        const url = window.URL.createObjectURL(blob);
        this.href = url;
        this.target = '_blank';
        this.download = title;
    };

    myDownload.click();
};

save.upload = async ({recordObj}) => {
    delete recordObj.id;

    // save to cloud
    const { newID } = await beadCloud({
        fn: 'saveToCloud',
        params: {recordObj, container: 'projects'}
    });
    if (!newID) {
        console.warn('error saving uploaded file to cloud');
        return;
    }
    recordObj.id = newID;
};

const getDefaultProjectName = () => lang.get('default project name', 'Untitled Project');

save.duplicate = async ({selectedFile, title=getDefaultProjectName()}) => {

    // get file from cloud
    const myFile = await beadCloud({
        fn: 'getItemFromId',
        params: {
            id: selectedFile.id,
            container: 'projects'
        }
    });

    if (myFile?.error) {
        console.warn('error getting file from cloud');
        return;
    }

    // change name, date created; delete id
    const recordObj = {
        ...myFile,
        title,
        lowerTitle: title.toLowerCase(),
        dateCreated: utils.YYYYMMDD(),
        dateModified: utils.YYYYMMDD()
    };
    delete recordObj.id;

    // save to cloud
    const { newID } = await beadCloud({
        fn: 'saveToCloud',
        params: {recordObj, container: 'projects'}
    });

    if (!newID) {
        console.warn('error saving file to cloud');
        return;
    }

    recordObj.id = newID;

    // insert the new file at the top of current browser list

    const resultToAdd = {};

    browserFields.forEach(key => {
        resultToAdd[key] = recordObj[key];
    });

    resultToAdd.sizeDescription = utils.getSizeDescription(recordObj);
    resultToAdd.libraryDisplayName = libraries[recordObj.settings.libraryIndex].displayName;
    resultToAdd.patternDisplayName = patterns.arr.find(p => p.name === recordObj.settings.patternName)?.displayName || '';

    const results = [...b3.store.state.browserResults];
    results.unshift(resultToAdd);
    b3.store.mySet('browserResults', results);

    // to do: we can selectively clear the cache to reduce bandwidth
    project.clearCache();
};

save.updateTagList = async () => {
    const tags = await beadCloud({
        fn: 'getAllMyTags',
        params: { currentUserId }
    });

    if (tags?.error) {
        console.warn('error getting tags');
        return;
    }

    b3.store.mySet('tags', tags);
};

save.updateMetaData = async ({
    selectedFile,
    title=getDefaultProjectName(),
    description,
    credits,
    tags,
    shouldUpdateTagList
}) => {

    const lowerTitle = title.toLowerCase();
    const instructions = selectedFile.instructions || '';

    // change file in cloud
    await beadCloud({
        fn: 'updateProjectMetaData',
        params: {title, lowerTitle, description, instructions, credits, tags, id: selectedFile.id}
    });

    // change file locally
    selectedFile.title = title;
    selectedFile.description = description;
    selectedFile.credits = credits;
    selectedFile.tags = tags;

    // change file in cache
    const recordObj = {
        title, description, credits, tags
    };
    project.updateResultCache({id: selectedFile.id, recordObj, fields: ['title', 'description', 'credits', 'tags']});

    // update browser title and settings if we renamed the currently open file
    if (selectedFile.id === settings.currentProjectId) {
        settings.setTitle(title);
        settings.description = description;
        settings.credits = credits;
        settings.tags = tags;
    }

    if (shouldUpdateTagList) save.updateTagList();

    project.clearCache(); // to do: we can selectively clear the cache to reduce bandwidth
};

save.updateProjectMetaData = async ({
    title=getDefaultProjectName(),
    description,
    credits,
    instructions=settings.instructions,
    tags,
    shouldUpdateTagList
}) => {

    const lowerTitle = title.toLowerCase();

    // change file in cloud
    await beadCloud({
        fn: 'updateProjectMetaData',
        params: {title, lowerTitle, description, instructions, credits, tags, id: settings.currentProjectId}
    });

    // change file locally
    settings.setTitle(title);
    settings.description = description;
    settings.instructions = instructions;
    settings.credits = credits;
    settings.tags = tags;

    // change file in cache
    const recordObj = {
        title, description, credits, tags
    };
    project.updateResultCache({id: settings.currentProjectId, recordObj, fields: ['title', 'description', 'credits', 'tags']});

    if (shouldUpdateTagList) save.updateTagList();

    project.clearCache(); // to do: we can selectively clear the cache to reduce bandwidth
};

save.startAutoSave = () => {
    if (save.autoSaveIntervalIndex) return;
    save.autoSaveIntervalIndex = setInterval(() => save.currentlyOpenFile(), 1000 * 60 * 5);
};